import { create } from 'zustand';
import { CHATBOT_MODE_CHATBOT, CHATBOT_MODE_RECOMMENDER, CHATBOT_WELCOME_NAVIGATION } from '@/utils/constants';

// Types
export interface Message {
  id: number | string;
  from: string;
  message: string | null;
  audio: string | null;
  score: number;
  createdAt: string;
  isStreaming?: boolean;
}

export interface StreamingMessage {
  content: string;
  channel: string;
  isComplete: boolean;
  createdAt: string;
}

export interface UserData {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  [key: string]: unknown;
}

export interface FeatureFlags {
  initialMessageChatbot?: string;
  initialMessageRecommender?: string;
  initialMessageRecommenderSkip?: string;
  [key: string]: unknown;
}

type ChatMode = typeof CHATBOT_MODE_CHATBOT | typeof CHATBOT_MODE_RECOMMENDER;

interface MessagesDict {
  [CHATBOT_MODE_CHATBOT]: Message[];
  [CHATBOT_MODE_RECOMMENDER]: Message[];
}

interface LoadingDict {
  [CHATBOT_MODE_CHATBOT]: boolean;
  [CHATBOT_MODE_RECOMMENDER]: boolean;
}

// Chat Store State
interface ChatState {
  // UI State
  isOpen: boolean;
  isFirstRender: boolean;
  navigationState: number; // 0: chatbot, 1: recommender
  welcomeNavigationState: number;
  isDropdownExpanded: boolean;
  isRecording: boolean;
  inputValue: string;
  detectedOs: string;
  openCount: number;

  // Connection State
  isConnected: boolean;
  connectionFailed: boolean;
  isCheckingBotStatus: boolean;
  chatbotStatusIsOnline: boolean;
  recommenderStatusIsOnline: boolean;
  skipMaintenanceMode: boolean;

  // Messages State
  messages: MessagesDict;
  isLoading: LoadingDict;
  streamingMessages: Record<string, StreamingMessage>;
  activeTools: Record<string, string[]>;
  unreadMessageCount: number;
  hasConversationHistory: boolean;
  isLoadingHistory: boolean;

  // Auth State
  isAuthenticated: boolean;
  hasPhoneNumber: boolean;
  token: string | null;
  socketToken: string | null;
  userData: UserData | null;
  portalId: string | null;

  // Reset State
  isResetting: boolean;
  isNewSessionAfterAuthChange: boolean;
  lastResetTimestamp: number;

  // Config
  configFeatureFlag: FeatureFlags;
  showProfileCompletionModal: boolean;

  // Computed
  currentMode: () => ChatMode;
  isStreamingActive: () => boolean;
}

// Chat Store Actions
interface ChatActions {
  // UI Actions
  setIsOpen: (open: boolean) => void;
  setIsFirstRender: (value: boolean) => void;
  setNavigationState: (state: number) => void;
  setWelcomeNavigationState: (state: number) => void;
  setIsDropdownExpanded: (expanded: boolean) => void;
  setIsRecording: (recording: boolean) => void;
  setInputValue: (value: string) => void;
  setDetectedOs: (os: string) => void;
  incrementOpenCount: () => void;

  // Connection Actions
  setIsConnected: (connected: boolean) => void;
  setConnectionFailed: (failed: boolean) => void;
  setIsCheckingBotStatus: (checking: boolean) => void;
  setChatbotStatusIsOnline: (online: boolean) => void;
  setRecommenderStatusIsOnline: (online: boolean) => void;
  setSkipMaintenanceMode: (skip: boolean) => void;

  // Messages Actions
  addMessage: (mode: ChatMode, message: Message) => void;
  updateMessage: (mode: ChatMode, messageId: string | number, updates: Partial<Message>) => void;
  clearMessages: (mode: ChatMode) => void;
  setAllMessages: (mode: ChatMode, messages: Message[]) => void;
  setIsLoading: (mode: ChatMode, loading: boolean) => void;
  setUnreadMessageCount: (count: number) => void;
  incrementUnreadCount: () => void;
  setHasConversationHistory: (has: boolean) => void;
  setIsLoadingHistory: (loading: boolean) => void;

  // Streaming Actions (Optimized)
  /**
   * Processes a streaming chunk in a single atomic operation.
   * Combines initStreamingMessage, appendToStreamingMessage, and message list update.
   * @param id - Message ID
   * @param content - New content chunk to append
   * @param channel - Chat mode channel
   * @param skipMaintenanceMode - Whether to append maintenance suffix
   */
  processStreamingChunk: (id: string, content: string, channel: string, skipMaintenanceMode: boolean) => void;
  
  /**
   * Finalizes a streaming message, marking it as complete and cleaning up state.
   * @param id - Message ID to finalize
   */
  finalizeStreamingMessage: (id: string) => void;

  // Streaming Actions (Legacy - Deprecated)
  /** @deprecated Use processStreamingChunk instead. Kept for backwards compatibility. */
  initStreamingMessage: (id: string, channel: string) => void;
  /** @deprecated Use processStreamingChunk instead. Kept for backwards compatibility. */
  appendToStreamingMessage: (id: string, content: string) => void;
  /** @deprecated Use finalizeStreamingMessage instead. Kept for backwards compatibility. */
  completeStreamingMessage: (id: string) => void;
  removeStreamingMessage: (id: string) => void;
  addActiveTool: (messageId: string, toolName: string) => void;
  clearActiveTools: (messageId: string) => void;

  // Auth Actions
  setIsAuthenticated: (auth: boolean) => void;
  setHasPhoneNumber: (has: boolean) => void;
  setToken: (token: string | null) => void;
  setSocketToken: (token: string | null) => void;
  setUserData: (data: UserData | null) => void;
  setPortalId: (id: string | null) => void;

  // Reset Actions
  setIsResetting: (resetting: boolean) => void;
  setIsNewSessionAfterAuthChange: (isNew: boolean) => void;
  setLastResetTimestamp: (timestamp: number) => void;

  // Config Actions
  setConfigFeatureFlag: (config: FeatureFlags) => void;
  setShowProfileCompletionModal: (show: boolean) => void;

  // Bulk Actions
  resetChat: () => void;
  clearAuthState: () => void;
}

// Initial state
const initialMessages: MessagesDict = {
  [CHATBOT_MODE_CHATBOT]: [],
  [CHATBOT_MODE_RECOMMENDER]: [],
};

const initialLoading: LoadingDict = {
  [CHATBOT_MODE_CHATBOT]: false,
  [CHATBOT_MODE_RECOMMENDER]: false,
};

// Create the store
export const useChatStore = create<ChatState & ChatActions>((set, get) => ({
  // UI State
  isOpen: false,
  isFirstRender: true,
  navigationState: 0,
  welcomeNavigationState: parseInt(localStorage.getItem(CHATBOT_WELCOME_NAVIGATION) || '0') || 0,
  isDropdownExpanded: false,
  isRecording: false,
  inputValue: '',
  detectedOs: '',
  openCount: 0,

  // Connection State
  isConnected: false,
  connectionFailed: false,
  isCheckingBotStatus: true,
  chatbotStatusIsOnline: true,
  recommenderStatusIsOnline: true,
  skipMaintenanceMode: false,

  // Messages State
  messages: { ...initialMessages },
  isLoading: { ...initialLoading },
  streamingMessages: {},
  activeTools: {},
  unreadMessageCount: 0,
  hasConversationHistory: false,
  isLoadingHistory: false,

  // Auth State
  isAuthenticated: false,
  hasPhoneNumber: false,
  token: null,
  socketToken: null,
  userData: null,
  portalId: null,

  // Reset State
  isResetting: false,
  isNewSessionAfterAuthChange: false,
  lastResetTimestamp: 0,

  // Config
  configFeatureFlag: {},
  showProfileCompletionModal: false,

  // Computed
  currentMode: () => {
    const state = get();
    if (state.navigationState === 0) return CHATBOT_MODE_CHATBOT;
    if (state.navigationState === 1) return CHATBOT_MODE_RECOMMENDER;
    return CHATBOT_MODE_CHATBOT;
  },

  isStreamingActive: () => {
    const state = get();
    return Object.values(state.streamingMessages).some(msg => !msg.isComplete);
  },

  // UI Actions
  setIsOpen: (open) => set({ isOpen: open }),
  setIsFirstRender: (value) => set({ isFirstRender: value }),
  setNavigationState: (state) => set({ navigationState: state }),
  setWelcomeNavigationState: (state) => set({ welcomeNavigationState: state }),
  setIsDropdownExpanded: (expanded) => set({ isDropdownExpanded: expanded }),
  setIsRecording: (recording) => set({ isRecording: recording }),
  setInputValue: (value) => set({ inputValue: value }),
  setDetectedOs: (os) => set({ detectedOs: os }),
  incrementOpenCount: () => set((state) => ({ openCount: state.openCount + 1 })),

  // Connection Actions
  setIsConnected: (connected) => set({ isConnected: connected }),
  setConnectionFailed: (failed) => set({ connectionFailed: failed }),
  setIsCheckingBotStatus: (checking) => set({ isCheckingBotStatus: checking }),
  setChatbotStatusIsOnline: (online) => set({ chatbotStatusIsOnline: online }),
  setRecommenderStatusIsOnline: (online) => set({ recommenderStatusIsOnline: online }),
  setSkipMaintenanceMode: (skip) => set({ skipMaintenanceMode: skip }),

  // Messages Actions
  addMessage: (mode, message) => set((state) => {
    const currentMessages = state.messages[mode];
    // Prevent duplicates
    if (currentMessages.length > 0) {
      const lastMsg = currentMessages[currentMessages.length - 1];
      if (lastMsg.from === message.from && lastMsg.message === message.message) {
        return state;
      }
    }
    return {
      messages: {
        ...state.messages,
        [mode]: [...currentMessages, message],
      },
    };
  }),

  updateMessage: (mode, messageId, updates) => set((state) => {
    const currentMessages = state.messages[mode];
    const index = currentMessages.findIndex(msg => msg.id === messageId);
    if (index === -1) return state;
    
    const updatedMessages = [...currentMessages];
    updatedMessages[index] = { ...updatedMessages[index], ...updates };
    
    return {
      messages: {
        ...state.messages,
        [mode]: updatedMessages,
      },
    };
  }),

  clearMessages: (mode) => set((state) => ({
    messages: {
      ...state.messages,
      [mode]: [],
    },
  })),

  setAllMessages: (mode, messages) => set((state) => ({
    messages: {
      ...state.messages,
      [mode]: messages,
    },
  })),

  setIsLoading: (mode, loading) => set((state) => ({
    isLoading: {
      ...state.isLoading,
      [mode]: loading,
    },
  })),

  setUnreadMessageCount: (count) => set({ unreadMessageCount: count }),
  incrementUnreadCount: () => set((state) => ({ unreadMessageCount: state.unreadMessageCount + 1 })),
  setHasConversationHistory: (has) => set({ hasConversationHistory: has }),
  setIsLoadingHistory: (loading) => set({ isLoadingHistory: loading }),

  // Streaming Actions (Optimized)
  /**
   * Processes a streaming chunk in a single atomic operation.
   * Combines initStreamingMessage, appendToStreamingMessage, and message list update.
   */
  processStreamingChunk: (id, content, channel, skipMaintenanceMode) => set((state) => {
    const mode = channel as typeof CHATBOT_MODE_CHATBOT | typeof CHATBOT_MODE_RECOMMENDER;
    const current = state.streamingMessages[id];
    const newContent = current ? current.content + content : content;
    const createdAt = current?.createdAt || new Date().toISOString();
    
    // Buscar mensaje existente en la lista
    const currentMessages = state.messages[mode];
    const existingIndex = currentMessages.findIndex(msg => msg.id === id);
    
    // Aplicar sufijo de maintenance si aplica
    let displayContent = newContent;
    if (skipMaintenanceMode && newContent) {
      displayContent = `${newContent}\n\n**[MAINTENANCE]**`;
    }
    
    // Preparar mensajes actualizados
    let updatedMessages: Message[];
    if (existingIndex >= 0) {
      updatedMessages = currentMessages.map((msg, idx) => 
        idx === existingIndex 
          ? { ...msg, message: displayContent, isStreaming: true }
          : msg
      );
    } else {
      updatedMessages = [...currentMessages, {
        id,
        from: 'bot',
        message: displayContent,
        audio: null,
        score: 0,
        createdAt,
        isStreaming: true,
      }];
    }
    
    return {
      streamingMessages: {
        ...state.streamingMessages,
        [id]: { content: newContent, channel, isComplete: false, createdAt },
      },
      messages: {
        ...state.messages,
        [mode]: updatedMessages,
      },
      isLoading: {
        ...state.isLoading,
        [mode]: false,
      },
    };
  }),

  /**
   * Finalizes a streaming message, marking it as complete and cleaning up state.
   */
  finalizeStreamingMessage: (id) => set((state) => {
    const current = state.streamingMessages[id];
    if (!current) return state;
    
    const mode = current.channel as typeof CHATBOT_MODE_CHATBOT | typeof CHATBOT_MODE_RECOMMENDER;
    const { [id]: _, ...restStreaming } = state.streamingMessages;
    const { [id]: __, ...restActiveTools } = state.activeTools;
    
    // Marcar mensaje como no-streaming
    const updatedMessages = state.messages[mode].map(msg =>
      msg.id === id ? { ...msg, isStreaming: false } : msg
    );
    
    return {
      streamingMessages: restStreaming,
      activeTools: restActiveTools,
      messages: {
        ...state.messages,
        [mode]: updatedMessages,
      },
      isLoading: {
        ...state.isLoading,
        [mode]: false,
      },
    };
  }),

  // Streaming Actions (Legacy - Deprecated)
  /** @deprecated Use processStreamingChunk instead. Kept for backwards compatibility. */
  initStreamingMessage: (id, channel) => set((state) => ({
    streamingMessages: {
      ...state.streamingMessages,
      [id]: {
        content: '',
        channel,
        isComplete: false,
        createdAt: new Date().toISOString(),
      },
    },
  })),

  /** @deprecated Use processStreamingChunk instead. Kept for backwards compatibility. */
  appendToStreamingMessage: (id, content) => set((state) => {
    const current = state.streamingMessages[id];
    if (!current) return state;
    return {
      streamingMessages: {
        ...state.streamingMessages,
        [id]: {
          ...current,
          content: current.content + content,
        },
      },
    };
  }),

  /** @deprecated Use finalizeStreamingMessage instead. Kept for backwards compatibility. */
  completeStreamingMessage: (id) => set((state) => {
    const current = state.streamingMessages[id];
    if (!current) return state;
    return {
      streamingMessages: {
        ...state.streamingMessages,
        [id]: {
          ...current,
          isComplete: true,
        },
      },
    };
  }),

  removeStreamingMessage: (id) => set((state) => {
    const { [id]: _, ...rest } = state.streamingMessages;
    return { streamingMessages: rest };
  }),

  addActiveTool: (messageId, toolName) => set((state) => {
    const currentTools = state.activeTools[messageId] || [];
    if (currentTools.includes(toolName)) return state;
    return {
      activeTools: {
        ...state.activeTools,
        [messageId]: [...currentTools, toolName],
      },
    };
  }),

  clearActiveTools: (messageId) => set((state) => {
    const { [messageId]: _, ...rest } = state.activeTools;
    return { activeTools: rest };
  }),

  // Auth Actions
  setIsAuthenticated: (auth) => set({ isAuthenticated: auth }),
  setHasPhoneNumber: (has) => set({ hasPhoneNumber: has }),
  setToken: (token) => set({ token }),
  setSocketToken: (token) => set({ socketToken: token }),
  setUserData: (data) => set({ userData: data }),
  setPortalId: (id) => set({ portalId: id }),

  // Reset Actions
  setIsResetting: (resetting) => set({ isResetting: resetting }),
  setIsNewSessionAfterAuthChange: (isNew) => set({ isNewSessionAfterAuthChange: isNew }),
  setLastResetTimestamp: (timestamp) => set({ lastResetTimestamp: timestamp }),

  // Config Actions
  setConfigFeatureFlag: (config) => set({ configFeatureFlag: config }),
  setShowProfileCompletionModal: (show) => set({ showProfileCompletionModal: show }),

  // Bulk Actions
  resetChat: () => set({
    inputValue: '',
    welcomeNavigationState: 0,
    hasConversationHistory: false,
    isLoadingHistory: false,
    isLoading: { ...initialLoading },
    messages: { ...initialMessages },
    unreadMessageCount: 0,
    isNewSessionAfterAuthChange: false,
    isDropdownExpanded: false,
    streamingMessages: {},
    activeTools: {},
  }),

  clearAuthState: () => set({
    messages: { ...initialMessages },
    hasConversationHistory: false,
    welcomeNavigationState: 0,
    isNewSessionAfterAuthChange: true,
  }),
}));

// Selector hooks for common patterns
export const useCurrentMode = () => useChatStore((state) => state.currentMode());
export const useIsStreamingActive = () => useChatStore((state) => state.isStreamingActive());
export const useMessages = (mode: ChatMode) => useChatStore((state) => state.messages[mode]);
export const useIsLoading = (mode: ChatMode) => useChatStore((state) => state.isLoading[mode]);

